"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, Award, ChevronRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Calendar, Sparkles, ArrowUpRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function Expertise() {
  const shouldReduceMotion = useReducedMotion();
  const keyMetrics = [{
    id: "metric-1",
    value: "+35%",
    label: "Avg. Direct Booking Growth",
    subtext: "Across managed properties"
  }, {
    id: "metric-2",
    value: "15+",
    label: "Years Expertise",
    subtext: "Hospitality Revenue & Tech"
  }, {
    id: "metric-3",
    value: "4.8x",
    label: "Return on OTA Audit",
    subtext: "Data-driven strategy ROI"
  }];
  const valuePillars = [{
    id: "pillar-1",
    text: "OTA Strategy Optimization"
  }, {
    id: "pillar-2",
    text: "Hotel Search Visibility & SEO"
  }, {
    id: "pillar-3",
    text: "Hospitality Tech Implementation"
  }];
  const containerAnimation = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemAnimation = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.1 : 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="heromarketingexpertise" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 md:py-28 lg:py-36">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 size-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 -left-32 size-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:24px_24px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <motion.div variants={containerAnimation} initial="hidden" animate="show" className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemAnimation} className="flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10 px-3 py-1 text-xs sm:text-sm font-medium gap-1.5">
                <Sparkles className="size-3.5 text-primary" />
                Led by Revenue Manager Sunil Singh
              </Badge>
              <span className="text-xs text-secondary-foreground/80 font-mono flex items-center gap-1">
                <Search className="size-3 text-primary" /> Hotel SEO & GEO Optimized
              </span>
            </motion.div>

            <motion.h1 variants={itemAnimation} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground leading-tight">
              Maximize Your Hotel’s Revenue with <span className="text-primary">Data-Driven</span> OTA Strategies
            </motion.h1>

            <motion.p variants={itemAnimation} className="text-base sm:text-lg text-secondary-foreground/90 max-w-2xl font-sans leading-relaxed">
              Empowering hotel owners and general managers to dominate online visibility, convert search traffic into direct bookings, and optimize hospitality tech stacks for peak profitability.
            </motion.p>

            {}
            <motion.div variants={itemAnimation} className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
              {valuePillars.map((pillar, i) => <div key={pillar.id} data-index={i} className="flex items-center gap-2 text-xs sm:text-sm font-medium text-secondary-foreground/90">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{pillar.text}</span>
                </div>)}
            </motion.div>

            {}
            <motion.div variants={itemAnimation} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-md group" asChild>
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                  Book a Consultation
                  <ChevronRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground font-medium text-base" asChild>
                <Link to="/ServicesPage">
                  Explore Services
                  <ArrowUpRight className="size-4 ml-1" />
                </Link>
              </Button>
            </motion.div>

            {}
            <motion.div variants={itemAnimation} className="pt-4 border-t border-secondary-foreground/15 flex items-center gap-4">
              <div className="flex -space-x-2 overflow-hidden">
                <div className="inline-block size-9 rounded-full ring-2 ring-secondary bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                  SS
                </div>
                <div className="inline-block size-9 rounded-full ring-2 ring-secondary bg-primary/30 flex items-center justify-center text-xs font-bold text-primary">
                  HR
                </div>
                <div className="inline-block size-9 rounded-full ring-2 ring-secondary bg-primary/40 flex items-center justify-center text-xs font-bold text-primary">
                  BB
                </div>
              </div>
              <div className="text-xs text-secondary-foreground/80">
                <p className="font-semibold text-secondary-foreground">Trusted Consultancy for Independent Hotels</p>
                <p>Tailored strategies for boutique stays, resorts & hotel groups</p>
              </div>
            </motion.div>

          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: shouldReduceMotion ? 0.1 : 0.7,
          delay: 0.2
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {}
              <div className="relative rounded-2xl overflow-hidden border border-primary/20 shadow-2xl bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_dashboard.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_dashboard.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_dashboard.png-wvc-sizes" alt="Hotel revenue analytics dashboard with rate parity and OTA growth charts" className="w-full h-auto object-cover aspect-4/3" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/20 to-transparent" />
                
                {}
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-card/95 backdrop-blur-md border border-border shadow-lg text-card-foreground">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold tracking-wider uppercase text-primary flex items-center gap-1">
                      <TrendingUp className="size-3.5" /> Direct Booking Surge
                    </span>
                    <span className="text-xs font-semibold text-muted-foreground">Real-time Strategy</span>
                  </div>
                  <p className="text-sm font-medium leading-snug">
                    Optimized OTA channel mix and search indexing drive sustained RevPAR growth.
                  </p>
                </div>
              </div>

              {}
              <motion.div animate={shouldReduceMotion ? {} : {
              y: [0, -8, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }} className="absolute -top-6 -right-4 sm:-right-6 hidden sm:block">
                <Card className="border-primary/30 bg-card text-card-foreground shadow-xl p-3 w-48 rounded-xl">
                  <CardContent className="p-0 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-primary/15 text-primary shrink-0">
                      <BarChart3 className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">RevPAR Index</p>
                      <p className="text-lg font-bold text-card-foreground">+28.4% YoY</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {}
              <motion.div animate={shouldReduceMotion ? {} : {
              y: [0, 8, 0]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }} className="absolute -bottom-6 -left-4 sm:-left-6 hidden sm:block">
                <Card className="border-primary/30 bg-card text-card-foreground shadow-xl p-3 w-52 rounded-xl">
                  <CardContent className="p-0 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-primary/15 text-primary shrink-0">
                      <Award className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">OTA Rate Parity</p>
                      <p className="text-sm font-bold text-card-foreground">100% Compliant</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

            </div>
          </motion.div>

        </div>

        {}
        <div className="mt-16 pt-12 border-t border-secondary-foreground/15 grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyMetrics.map((metric, i) => <div key={metric.id} data-index={i} className="flex items-start gap-4 p-4 rounded-xl bg-secondary-foreground/5 border border-secondary-foreground/10">
              <div className="p-3 rounded-lg bg-primary/10 text-primary">
                <TrendingUp className="size-6" />
              </div>
              <div>
                <span className="text-2xl sm:text-3xl font-extrabold text-primary font-serif tracking-tight">
                  {metric.value}
                </span>
                <h2 className="text-sm font-semibold text-secondary-foreground mt-0.5">
                  {metric.label}
                </h2>
                <p className="text-xs text-secondary-foreground/70 mt-0.5">
                  {metric.subtext}
                </p>
              </div>
            </div>)}
        </div>

      </div>
    </section>;
}